import React, { useState } from 'react';
import { useDevices } from '../../context/DeviceContext';
import { valveStateOf, VALVE_LABEL } from '../../domain/device';
import DevicesIcon from '@mui/icons-material/Devices';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import SignalWifiStatusbar4BarIcon from '@mui/icons-material/SignalWifiStatusbar4Bar';
import SignalWifiOffIcon from '@mui/icons-material/SignalWifiOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';
import './DeviceSelector.css';

const DeviceSelector = ({ showTitle = true, compact = false }) => {
  const { 
    devices, 
    selectedDevice, 
    loading, 
    error, 
    selectDevice, 
    refreshData 
  } = useDevices();
  
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshData();
    } finally {
      setRefreshing(false);
    }
  };

  const getDeviceStatusIcon = (device) => {
    const now = Date.now() / 1000;
    let lastSeen = device.last_seen || 0;
    
    // Si lastSeen parece ser un timestamp en milisegundos (muy grande), convertir a segundos
    if (lastSeen > 1000000000000) {
      lastSeen = lastSeen / 1000;
    }
    
    if (lastSeen === 0) {
      return <ErrorIcon className="status-icon offline" title="Nunca conectado" />;
    }
    
    const timeSinceLastSeen = now - lastSeen;
    
    // Debug: mostrar información en consola
    console.log(`🔍 Estado dispositivo ${device.device_id}:`, {
      now,
      lastSeen,
      timeSinceLastSeen,
      status: timeSinceLastSeen < 120 ? 'online' : 'offline'
    });
    
    // Considerar en línea si vio actividad en los últimos 2 minutos (120 segundos)
    if (timeSinceLastSeen < 120 && timeSinceLastSeen >= 0) {
      return <CheckCircleIcon className="status-icon online" title="En línea" />;
    } else {
      return <ErrorIcon className="status-icon offline" title="Desconectado" />;
    }
  };

  const getSignalIcon = (device) => {
    // Determinar si el dispositivo está en línea primero
    const now = Date.now() / 1000;
    let lastSeen = device.last_seen || 0;
    
    // Si lastSeen parece ser un timestamp en milisegundos, convertir a segundos
    if (lastSeen > 1000000000000) {
      lastSeen = lastSeen / 1000;
    }
    
    const timeSinceLastSeen = now - lastSeen;
    const isOnline = (timeSinceLastSeen < 120 && timeSinceLastSeen >= 0);
    
    // Si está offline, mostrar sin señal
    if (!isOnline) {
      return <SignalWifiOffIcon className="signal-icon weak" title="Sin conexión" />;
    }
    
    // Si está online, determinar por RSSI
    const rssi = device.wifi_rssi || -100;
    
    if (rssi > -60) {
      return <SignalWifiStatusbar4BarIcon className="signal-icon strong" title="Señal fuerte" />;
    } else if (rssi > -80) {
      return <SignalWifiStatusbar4BarIcon className="signal-icon medium" title="Señal media" />;
    } else {
      return <SignalWifiStatusbar4BarIcon className="signal-icon weak" title="Señal débil" />;
    }
  };

  const formatLastSeen = (timestamp) => {
    if (!timestamp || timestamp === 0) {
      return 'Nunca conectado';
    }
    
    let ts = timestamp;
    // Si timestamp parece ser en milisegundos (muy grande), convertir a segundos
    if (ts > 1000000000000) {
      ts = ts / 1000;
    }
    
    const now = Date.now() / 1000;
    const diff = now - ts;
    
    if (diff < 0) return 'Ahora'; // Timestamp futuro
    if (diff < 30) return 'En línea'; // Menos de 30 segundos
    if (diff < 60) return 'Hace menos de 1 min';
    if (diff < 3600) return `Hace ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `Hace ${Math.floor(diff / 3600)} h`;
    if (diff < 604800) return `Hace ${Math.floor(diff / 86400)} días`; // Menos de 7 días
    return 'Hace más de 7 días';
  };

  if (loading && devices.length === 0) {
    return (
      <div className={`device-selector ${compact ? 'compact' : ''}`}>
        {showTitle && <h3>Mis Dispositivos</h3>}
        <div className="loading-message">
          <WaterDropIcon className="loading-icon" />
          <span>Cargando dispositivos...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`device-selector ${compact ? 'compact' : ''}`}>
        {showTitle && <h3>Mis Dispositivos</h3>}
        <div className="error-message">
          <ErrorIcon />
          <span>Error: {error}</span>
          <button onClick={handleRefresh} className="retry-btn">
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (devices.length === 0) {
    return (
      <div className={`device-selector ${compact ? 'compact' : ''}`}>
        {showTitle && <h3>Mis Dispositivos</h3>}
        <div className="no-devices-message">
          <DevicesIcon className="no-devices-icon" />
          <h4>No tienes dispositivos asignados</h4>
          <p>Contacta al administrador para que te asigne dispositivos.</p>
          <button onClick={handleRefresh} className="refresh-btn">
            <RefreshIcon />
            Actualizar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`device-selector ${compact ? 'compact' : ''}`}>
      {showTitle && (
        <div className="device-selector-header">
          <h3>Mis Dispositivos</h3>
          <button 
            onClick={handleRefresh} 
            className={`refresh-btn ${refreshing ? 'refreshing' : ''}`}
            disabled={refreshing}
            title="Actualizar dispositivos"
          >
            <RefreshIcon />
          </button>
        </div>
      )}
      
      <div className="devices-list">
        {devices.map((device) => (
          <div
            key={device.device_id}
            className={`device-card ${selectedDevice?.device_id === device.device_id ? 'selected' : ''}`}
            onClick={() => selectDevice(device)}
          >
            <div className="device-header">
              <div className="device-info">
                <h4 className="device-name">
                  {device.device_name || device.device_id}
                </h4>
                <span className="device-location">
                  {device.location || 'Sin ubicación'}
                </span>
              </div>
              <div className="device-status">
                {getDeviceStatusIcon(device)}
                {getSignalIcon(device)}
              </div>
            </div>
            
            {!compact && (
              <div className="device-details">
                <div className="device-stat">
                  <span className="stat-label">Estado válvula:</span>
                  {/* Mismo dato y misma etiqueta que la pantalla de válvula:
                      se derivan de domain/device.js para que la lista y el
                      detalle no puedan decir cosas distintas del mismo equipo. */}
                  <span className={`stat-value valve-${valveStateOf(device)}`}>
                    {VALVE_LABEL[valveStateOf(device)]}
                  </span>
                </div>
                
                <div className="device-stat">
                  <span className="stat-label">Última conexión:</span>
                  <span className="stat-value">
                    {formatLastSeen(device.last_seen)}
                  </span>
                </div>
                
                {device.flow_liters !== undefined && (
                  <div className="device-stat">
                    <span className="stat-label">Flujo total:</span>
                    <span className="stat-value">
                      {parseFloat(device.flow_liters || 0).toFixed(2)} L
                    </span>
                  </div>
                )}
              </div>
            )}
            
            <div className="device-id">
              ID: {device.device_id}
            </div>
          </div>
        ))}
      </div>
      
      {selectedDevice && (
        <div className="selected-device-info">
          <h4>Dispositivo Seleccionado:</h4>
          <span>{selectedDevice.device_name || selectedDevice.device_id}</span>
        </div>
      )}
    </div>
  );
};

export default DeviceSelector;
